import { useState } from "react";
import { useTranslation } from "react-i18next";

import { US_STATES, type UsStateCode } from "@/lib/states";

/**
 * apps/web :: components/onboarding :: CurrentFocusStateStep
 *
 * Step 4 of the wizard. Lets the user pick a single "current focus state"
 * out of the target_states list they just chose in step 3. We can't call
 * `<StatePicker />` directly here because it always renders the full MVP
 * catalog (that's its job — the onboarding-specific filter would creep
 * into every caller). Instead this component renders a radio-style list
 * scoped to the target_states subset, reusing the same `state-picker-card-*`
 * testids so downstream tests stay compatible.
 *
 * Single-target-state skip is handled by the wizard (ROADS-505) — this
 * component is never rendered when `targetStates.length === 1`. The
 * `isSolo` branch + the body-text variant below is preserved for the
 * theoretical case where a future caller bypasses the wizard, but it's
 * not on any current render path.
 */
export interface CurrentFocusStateStepProps {
  targetStates: UsStateCode[];
  value: UsStateCode | null;
  onNext: (code: UsStateCode) => void;
  onBack: () => void;
}

export default function CurrentFocusStateStep({
  targetStates,
  value,
  onNext,
  onBack,
}: CurrentFocusStateStepProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<UsStateCode | null>(value);

  const filteredStates = US_STATES.filter((s) =>
    targetStates.includes(s.code),
  );
  const canProceed = draft !== null && targetStates.includes(draft);
  const isSolo = targetStates.length === 1;

  return (
    <div className="space-y-md">
      <header>
        <h2 className="font-display text-xl font-semibold text-brand-primary">
          {t("onboarding.currentFocusState.title")}
        </h2>
        <p className="mt-xs text-sm text-neutral-700">
          {isSolo
            ? t("onboarding.currentFocusState.body")
            : t("onboarding.currentFocusState.bodyMulti")}
        </p>
      </header>

      <div
        role="radiogroup"
        aria-label={t("onboarding.currentFocusState.groupLabel")}
        className="grid grid-cols-1 gap-sm sm:grid-cols-2 lg:grid-cols-3"
      >
        {filteredStates.map((s) => {
          const selected = draft === s.code;
          const localisedName = t(`state.${s.code.toLowerCase()}.name`, {
            defaultValue: s.name,
          });
          return (
            <button
              key={s.code}
              type="button"
              role="radio"
              aria-checked={selected}
              data-testid={`state-picker-card-${s.code}`}
              data-state-code={s.code}
              onClick={() => setDraft(s.code)}
              className={[
                "flex flex-col items-start gap-xs rounded-md border bg-white p-sm text-left transition-colors",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary",
                selected
                  ? "border-brand-primary bg-brand-primary/5"
                  : "border-brand-primary/20 hover:border-brand-primary/40",
              ].join(" ")}
            >
              <span
                aria-hidden="true"
                className="inline-flex h-6 min-w-[2rem] items-center justify-center rounded-md bg-brand-primary/10 px-xs text-xs font-semibold text-brand-primary"
              >
                {s.code}
              </span>
              <span className="text-sm font-semibold text-brand-primary">
                {localisedName}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-sm pt-md">
        <button
          type="button"
          data-testid="onboarding-back"
          onClick={onBack}
          className="inline-flex items-center justify-center rounded-md border border-brand-primary/20 bg-white px-md py-sm text-sm font-medium text-brand-primary transition-colors hover:border-brand-primary/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {t("onboarding.back")}
        </button>
        <button
          type="button"
          data-testid="onboarding-next"
          disabled={!canProceed}
          onClick={() => {
            if (draft !== null) onNext(draft);
          }}
          className="inline-flex items-center justify-center rounded-md bg-brand-primary px-md py-sm text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t("onboarding.next")}
        </button>
      </div>
    </div>
  );
}
